// v2.16.2 — EmploiCard : carte d'offre d'emploi.
// Mêmes dimensions/structure que DivertissementCard (240×~306, image 110px).
// Badge plateforme + badge contract_type. 2 lignes de boutons : génériques (apply/copy/share) + Page dédiée.

import { useState } from "react";
import { toast } from "sonner";

const CONTRACT_COLORS = {
  CDI:        { bg: "#16A34A", color: "#fff" },
  CDD:        { bg: "#F59E0B", color: "#0F172A" },
  Stage:      { bg: "#3B82F6", color: "#fff" },
  Freelance:  { bg: "#7C3AED", color: "#fff" },
  Apprentissage: { bg: "#06B6D4", color: "#0F172A" },
};

const ACCENT = "#10B981"; // vert/teal global mode Emploi

function fmtDateRel(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  const days = Math.floor((Date.now() - t) / 86_400_000);
  if (days < 1) return "aujourd'hui";
  if (days === 1) return "il y a 1j";
  if (days < 7) return `il y a ${days}j`;
  if (days < 30) return `il y a ${Math.floor(days / 7)}sem`;
  return `il y a ${Math.floor(days / 30)} mois`;
}

export default function EmploiCard({ item, onDetail }) {
  const [hovering, setHovering] = useState(false);
  if (!item) return null;
  const contractStyle = CONTRACT_COLORS[item.contract_type] || { bg: "#64748B", color: "#fff" };

  const apply = () => {
    if (!item.apply_url) { toast.error("URL plateforme indisponible"); return; }
    window.open(item.apply_url, "_blank", "noopener,noreferrer");
  };

  const copyOffer = async () => {
    const text = `💼 ${item.title}\n🏢 ${item.company}\n📍 ${item.location_text}\n💰 ${item.salary_display}\n📋 ${item.contract_type} · ${item.experience_level}\n\n🔗 Postuler : ${item.apply_url}\nSource : ${item.platform_name}`;
    try {
      await navigator.clipboard.writeText(text);
      toast.success("📋 Offre copiée dans le presse-papier");
    } catch (_) { toast.error("Copie impossible"); }
  };

  const share = () => {
    const msg = `💼 Offre d'emploi : ${item.title} chez ${item.company} (${item.location_text}, ${item.contract_type})\n💰 ${item.salary_display}\n\n🔗 Postuler : ${item.apply_url}\n\nVu sur AYA 🇨🇮`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  const goDetail = () => {
    if (onDetail) onDetail(item);
    else window.open(`/jobs/${item.id}`, "_blank");
  };

  return (
    <article
      data-testid={`emploi-card-${item.id}`}
      className="civ-mode-card"
      onMouseEnter={() => setHovering(true)}
      onMouseLeave={() => setHovering(false)}
      style={{
        position: "relative", borderRadius: 14, overflow: "hidden",
        background: "#0f172a",
        border: `1px solid ${hovering ? ACCENT : "rgba(255,255,255,0.08)"}`,
        boxShadow: "0 1px 3px rgba(0,0,0,0.18), 0 6px 14px rgba(0,0,0,0.22)",
        display: "flex", flexDirection: "column",
        transition: "transform 200ms ease, border-color 200ms ease",
        transform: hovering ? "translateY(-2px)" : "translateY(0)",
        flex: "0 0 240px", width: 240, scrollSnapAlign: "start",
      }}
    >
      {/* HEADER : bandeau gradient avec badges plateforme + contract */}
      <div style={{
        position: "relative", height: 110,
        background: `linear-gradient(135deg, ${ACCENT}E6, ${ACCENT}66)`,
        display: "flex", flexDirection: "column", justifyContent: "space-between",
        padding: 10,
      }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 6 }}>
          <span style={{
            background: "rgba(15,23,42,0.85)", color: "#fff",
            padding: "3px 8px", borderRadius: 999,
            fontSize: 10, fontWeight: 700,
          }}>📰 {item.platform_name}</span>
          <span style={{
            background: contractStyle.bg, color: contractStyle.color,
            padding: "3px 8px", borderRadius: 999,
            fontSize: 10, fontWeight: 800, letterSpacing: "0.04em",
          }}>{item.contract_type}</span>
        </div>
        <div style={{
          fontSize: 11, color: "rgba(255,255,255,0.9)",
          fontWeight: 600, textShadow: "0 1px 2px rgba(0,0,0,0.3)",
        }}>
          {item.category_emoji} {item.experience_level} · {fmtDateRel(item.posted_at)}
        </div>
      </div>

      {/* BODY */}
      <div style={{ padding: "10px 12px", display: "flex", flexDirection: "column", gap: 4, flex: 1 }}>
        <div style={{ fontSize: 13.5, fontWeight: 700, color: "#fff", lineHeight: 1.25 }} title={item.title}>
          {item.title}
        </div>
        <div style={{ fontSize: 11.5, color: "rgba(255,255,255,0.65)" }}>
          🏢 {item.company}
        </div>
        <div style={{ fontSize: 11, color: "rgba(255,255,255,0.55)" }}>
          📍 {item.location_text}
        </div>
        {item.salary_display && (
          <div style={{ fontSize: 11.5, color: ACCENT, fontWeight: 700, marginTop: 2 }}>
            💰 {item.salary_display}
          </div>
        )}
        {Array.isArray(item.skills) && item.skills.length > 0 && (
          <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 4 }}>
            {item.skills.slice(0, 3).map((s) => (
              <span key={s} style={{
                background: "rgba(255,255,255,0.07)",
                color: "rgba(255,255,255,0.8)",
                border: "1px solid rgba(255,255,255,0.12)",
                padding: "1px 6px", borderRadius: 6,
                fontSize: 10, fontWeight: 600,
              }}>{s}</span>
            ))}
          </div>
        )}

        {/* Ligne 1 — Apply + Copy + Share */}
        <div style={{ marginTop: 6, display: "flex", gap: 6, flexWrap: "wrap" }}>
          <button type="button" data-testid="emploi-card-apply-btn" onClick={apply} style={{
            background: ACCENT, color: "#fff", border: "none",
            padding: "5px 10px", borderRadius: 8, fontSize: 11.5, fontWeight: 700, cursor: "pointer",
          }}>🔗 Postuler</button>
          <button type="button" data-testid="emploi-card-copy-btn" onClick={copyOffer} style={{
            background: "rgba(255,255,255,0.06)", color: "#CBD5E1",
            border: "1px solid rgba(255,255,255,0.18)",
            padding: "5px 8px", borderRadius: 8, fontSize: 11.5, cursor: "pointer",
          }} title="Copier l'offre">📋</button>
          <button type="button" data-testid="emploi-card-share-btn" onClick={share} style={{
            background: "#25D366", color: "#fff", border: "none",
            padding: "5px 8px", borderRadius: 8, fontSize: 11.5, cursor: "pointer",
          }} title="Partager via WhatsApp">📤</button>
        </div>
        {/* Ligne 2 — Page dédiée */}
        <div style={{ marginTop: 6 }}>
          <button type="button" data-testid="emploi-card-detail-btn" onClick={goDetail} style={{
            width: "100%",
            background: "rgba(16,185,129,0.12)", color: ACCENT,
            border: `1px solid ${ACCENT}66`,
            padding: "5px 10px", borderRadius: 8, fontSize: 11.5, fontWeight: 600, cursor: "pointer",
          }}>📄 Page dédiée</button>
        </div>
      </div>
    </article>
  );
}
